/**
 * Shared cash-offer form fields used by both stage-level (SortableStageCard)
 * and plan-level (PlanCashOfferPanel) offers.
 *
 * The two variants differ only in accent colour and save-button text,
 * controlled via the `variant` prop.
 */

export interface CashOfferFormFields {
  amount: string
  deadline: string
  method: string
  note: string
}

export function CashOfferForm({
  fields,
  saving,
  hasSavedOffer,
  variant,
  onFieldChange,
  onSave,
  onClear,
}: {
  fields: CashOfferFormFields
  saving: boolean
  hasSavedOffer: boolean
  /** 'stage' → amber accent, 'plan' → green accent */
  variant: 'stage' | 'plan'
  onFieldChange: (patch: Partial<CashOfferFormFields>) => void
  onSave: () => void
  onClear: () => void
}) {
  const accent = variant === 'stage' ? 'amber' : 'green'
  const ringClass = `focus:ring-${accent}-400`
  const saveClass = variant === 'stage'
    ? 'rounded bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60'
    : 'rounded bg-green-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-800 disabled:opacity-60'
  const showClearBtn = hasSavedOffer || fields.amount || fields.note || fields.deadline

  return (
    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">Цена за наличные, ₽</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="100"
          className={`rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 focus:outline-none focus:ring-1 ${ringClass}`}
          value={fields.amount}
          onChange={(e) => onFieldChange({ amount: e.target.value })}
          placeholder="например, 80 000"
          disabled={saving}
        />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">Действует до</span>
        <input
          type="date"
          className={`rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 focus:outline-none focus:ring-1 ${ringClass}`}
          value={fields.deadline}
          onChange={(e) => onFieldChange({ deadline: e.target.value })}
          disabled={saving}
        />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">Способ оплаты</span>
        <input
          type="text"
          className={`rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 focus:outline-none focus:ring-1 ${ringClass}`}
          value={fields.method}
          onChange={(e) => onFieldChange({ method: e.target.value })}
          placeholder="наличные / СБП"
          disabled={saving}
        />
      </label>
      <label className="flex flex-col gap-0.5 sm:col-span-2 lg:col-span-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">Примечание</span>
        <input
          type="text"
          className={`rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 focus:outline-none focus:ring-1 ${ringClass}`}
          value={fields.note}
          onChange={(e) => onFieldChange({ note: e.target.value })}
          placeholder="При оплате наличными до …"
          disabled={saving}
        />
      </label>
      <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-4">
        <button
          type="button"
          className={saveClass}
          onClick={onSave}
          disabled={saving}
        >
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
        {showClearBtn ? (
          <button
            type="button"
            className="rounded border border-slate-300 bg-white px-3 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-60"
            onClick={onClear}
            disabled={saving}
          >
            Убрать
          </button>
        ) : null}
      </div>
    </div>
  )
}
